import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import get from 'lodash/get';

import { WorkspaceMember } from '@futrli-sdk/people';

import { useOrganistionName } from '../../../../hooks/useOrganisationName';
import { AuthStoreState } from '../../../../core/types';
import { useRouteIds } from '../../../../hooks/useRouteIds';

const FIELD_IDS = {
  SEND_TO: 'email-pdf-send-to',
  SUBJECT: 'email-pdf-subject',
  BODY: 'email-pdf-body'
};

const MAX_NUMBER_OF_USERS = 30;

export const useEmailPdfForm = (reportName: string) => {
  const intl = useIntl();
  const { workspaceId, organisationId } = useRouteIds();
  const organisationName = useOrganistionName(workspaceId, organisationId);

  const fullName = useSelector((store: AuthStoreState) => {
    const user = store.authReducers.authenticatedUser;
    const givenName = get(user, 'attributes.given_name', '');
    const familyName = get(user, 'attributes.family_name', '');
    return !familyName ? givenName : `${givenName} ${familyName}`;
  });

  const [selectedUsers, setSelectedUsers] = useState<WorkspaceMember[]>([]);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [validationErrors, setValidationErrors] = useState<Record<string, { id: string; message: string }>>({});
  const hasBeenValidated = useRef(false);

  useEffect(() => {
    setEmailSubject(
      intl.formatMessage(
        {
          id: 'predict.default_report_email_subject',
          defaultMessage: 'You have a new report for {organisationName}'
        },
        { organisationName }
      )
    );
  }, [organisationName, intl]);

  useEffect(() => {
    setEmailBody(
      intl.formatMessage(
        {
          id: 'predict.default_report_email_body_v3',
          defaultMessage: `Hello,\n\nI’ve prepared the report {reportName} for you. Please find it attached as a PDF.\n\nAll the best,\n\n{fullName}`
        },
        { reportName, fullName }
      )
    );
  }, [fullName, reportName, intl]);

  const validate = useCallback(() => {
    const errors: Record<string, { id: string; message: string }> = {};

    if (selectedUsers.length === 0) {
      errors[FIELD_IDS.SEND_TO] = {
        id: FIELD_IDS.SEND_TO,
        message: intl.formatMessage({
          id: 'predict.email_pdf_select_recipients',
          defaultMessage: 'Please select at least one recipient'
        })
      };
    } else if (selectedUsers.length > MAX_NUMBER_OF_USERS) {
      errors[FIELD_IDS.SEND_TO] = {
        id: FIELD_IDS.SEND_TO,
        message: intl.formatMessage(
          { id: 'predict.email_pdf_max_recipients', defaultMessage: 'Maximum {max} recipients allowed' },
          { max: MAX_NUMBER_OF_USERS }
        )
      };
    }

    if (!emailSubject.trim()) {
      errors[FIELD_IDS.SUBJECT] = {
        id: FIELD_IDS.SUBJECT,
        message: intl.formatMessage({
          id: 'predict.email_pdf_subject_required',
          defaultMessage: 'Email subject is required'
        })
      };
    }

    if (!emailBody.trim()) {
      errors[FIELD_IDS.BODY] = {
        id: FIELD_IDS.BODY,
        message: intl.formatMessage({
          id: 'predict.email_pdf_body_required',
          defaultMessage: 'Email body is required'
        })
      };
    }

    setValidationErrors(errors);
    hasBeenValidated.current = true;
    return Object.keys(errors).length === 0;
  }, [selectedUsers, emailSubject, emailBody, intl]);

  const revalidate = useCallback(() => {
    if (hasBeenValidated.current) {
      validate();
    }
  }, [validate]);

  return {
    selectedUsers,
    setSelectedUsers,
    emailSubject,
    setEmailSubject,
    emailBody,
    setEmailBody,
    validationErrors,
    validate,
    revalidate,
    FIELD_IDS
  };
};
